// MATERIAL UI
import {
  AccordionSummary,
  Button,
  Link,
  MenuItem,
  Pagination,
  Select,
  Stack,
  useTheme,
} from "@mui/material";

// REACT
import { useEffect, useState } from "react";

// COMPONENTS
import {
  DropDownRedArrow,
  StyledAccordion,
  StyledAccordionDetails,
  StyledHelperContainer,
  StyledHelperLabel,
  StyledHelperTitle,
  StyledLabel,
  StyledRedHelperText,
  StyledTab,
  StyledTableTitle,
  StyledTitle,
  TableContainer,
} from "./styled-components/TableStyledComponents";
import CancelModal from "./CancelModal";
import OrderDetails from "./OrderDetails";
import YourPlan from "./YourPlan";
import { CustomLoadingComponent } from "./styled-components/LoadingDots";

// ICONS
import DownArrowIcon from "src/icons/portfolio/DownArrowIcon";

// REDUX
import api from "src/store/features/api";
import { DcaStrategyDto } from "src/store/features/api/generated";
import { useAppDispatch } from "src/store/store";

// UTIL FUNCTIONS
import { dateFormatter } from "src/utils/helpers/DateAndTimeHelpers";
import { setSelectedStrategy } from "src/store/features/dca.slice";
import { dynamicUnitFormatter, formatCurrency } from "src/utils/helpers/PriceAndUnitsHelpers";

// AMPLITUDE
import { trackEvent } from "src/Amplitude";

// HOOKS
import usePermission from "src/utils/hooks/usePermission";

export const FilterSelection = (props: {
  filterState: number;
  setFilterState: (filterState: number) => void;
  allStatuses: string[];
  variant?: "mobile";
}) => {
  const { filterState, setFilterState, allStatuses, variant } = props;
  const [isOpen, setIsOpen] = useState(false);
  const theme = useTheme();

  const mobileVariant = variant === "mobile";
  return (
    <Select
      open={isOpen}
      value={filterState}
      onChange={(event) => {
        setFilterState(+event.target.value);
        setIsOpen(false);
      }}
      MenuProps={{
        PaperProps: {
          sx: {
            borderRadius: "0px 0px 16px 16px",
            overflow: "hidden",
            boxShadow: "0px 12px 24px rgba(216, 216, 218, 0.80)",
          },
        },
      }}
      sx={{
        textAlign: "center",
        height: mobileVariant ? "40px" : "24px",
        width: mobileVariant ? "100%" : "auto",
        borderRadius: mobileVariant ? "10px" : "20px",
        margin: "10px 0px",
        position: "relative",
      }}
      onOpen={() => setIsOpen(true)}
      onClose={() => setIsOpen(false)}
      IconComponent={() => (
        <span
          onClick={(e) => {
            e.stopPropagation(); // Prevent dropdown from closing on icon click
            setIsOpen((prev) => !prev); // Toggle dropdown open/close
          }}
          style={{
            position: "absolute",
            right: "10px",
            top: isOpen ? "45%" : "50%",
            transformOrigin: "center",
            transform: `translateY(-50%) rotate(${isOpen ? "180deg" : "0deg"})`,
            cursor: "pointer",
          }}>
          <DownArrowIcon color={theme.palette.mode === "light" ? undefined : "#fff"} />
        </span>
      )}>
      {allStatuses.map((status: string, index: number) => (
        <MenuItem key={index} value={index}>
          {status}
        </MenuItem>
      ))}
    </Select>
  );
};

export default function StrategiesTable() {
  const [openCancelModal, setOpenCancelModal] = useState(false);

  const [page, setPage] = useState(1);

  const [filterState, setFilterState] = useState(1);

  const { isGuest } = usePermission();

  const statuses = ["Active", "Completed", "Cancelled"];
  const allStatuses = ["All", ...statuses];
  const [isFirstCheckDone, setIsFirstCheckDone] = useState(false); // Tracks if the first check for strategies object has been performed

  const theme = useTheme();
  const {
    data: { strategies, pageCount } = {},
    isFetching,
    refetch,
  } = api.useGetDcaStrategiesQuery(
    {
      page,
      filters: allStatuses[filterState] === "All" ? statuses : [allStatuses[filterState]],
    },
    {
      skip: isGuest,
    },
  );

  useEffect(() => {
    setPage(1);
  }, [filterState]);

  // useEffect to auto-set status to 'All' when no strategies are returned for 'Active', and make sure refetch the data as well as it is only called once.
  useEffect(() => {
    if (!isFirstCheckDone && strategies?.length === 0) {
      setFilterState(0); // Reset filter to "All" when no strategies are returned for active
      refetch(); // Refetch strategies after changing the status to all
      setIsFirstCheckDone(true); // Ensure this runs only once
    }
  }, [isFirstCheckDone, strategies]);

  const TableRow = ({ strategy, iterator }: { strategy: DcaStrategyDto; iterator: number }) => {
    const [tabValue, setTabValue] = useState(0);

    const theme = useTheme();
    const dispatch = useAppDispatch();

    let tabs = ["Your Plan", "Orders"];

    const handleChange = (newValue: number) => {
      setTabValue(newValue);
      trackEvent("Switched tab", { tab: tabs[newValue] }, "DCA");
    };

    const {
      amount,
      buyCurrency: { code: fiatToken, logoUrl },
      sellCurrency: { code: sellToken },
      frequency,
      startDate,
      pastTriggers,
      totalTriggers,
      status,
      accumulatedBalance,
    } = strategy;

    if (status === "Cancelled" && !pastTriggers) tabs = ["Your Plan"];

    const CustomTabs = () => {
      return (
        <Stack direction="row" mb={2} ml={0.5} gap={2}>
          {tabs.map((tab, index) => (
            <StyledTab selected={tabValue === index} onClick={() => handleChange(index)}>
              {tab}
            </StyledTab>
          ))}
        </Stack>
      );
    };

    return (
      <StyledAccordion elevation={0} disableGutters={true} defaultExpanded={!iterator}>
        <AccordionSummary
          sx={{
            padding: "6px 12px",
            margin: 0,
            "&:hover": { background: theme.palette.primary.contrastText, borderRadius: "10px" },
          }}
          expandIcon={
            <div style={{ margin: "0px 10px" }}>
              <DropDownRedArrow />
            </div>
          }>
          <Stack justifyContent={"space-between"} flex="auto" gap={0.5}>
            <div style={{ display: "flex", gap: "4px" }}>
              <img alt="crypto logo" src={logoUrl} style={{ width: "24px", height: "24px" }} />
              <StyledTitle>{`${formatCurrency(
                amount,
                sellToken,
              )} ${fiatToken} ${frequency}`}</StyledTitle>
            </div>
            <StyledLabel>{dateFormatter(startDate)}</StyledLabel>
          </Stack>
          <Stack textAlign="right" gap={0.5}>
            <StyledTitle>
              {status === "Cancelled" ? "Cancelled  " : `${pastTriggers}/${totalTriggers ?? "-"}  `}
            </StyledTitle>
            <StyledLabel>{`${dynamicUnitFormatter(
              accumulatedBalance ?? 0,
              0,
            )} ${fiatToken}`}</StyledLabel>
          </Stack>
        </AccordionSummary>

        <StyledAccordionDetails>
          {/* Tabs */}
          <CustomTabs />
          {/* Your Plan Tab */}
          {tabValue === 0 && <YourPlan strategy={strategy} />}
          {/* Order Details Tab */}
          {tabValue === 1 && <OrderDetails strategy={strategy} />}
          {/* Cancel Button */}
          {status === "Active" && (
            <Button
              disableRipple
              variant="outlined"
              fullWidth
              sx={{
                borderRadius: "8px",
                textTransform: "capitalize",
                backgroundColor: theme.palette.secondary.main,
                color: theme.palette.mode === "light" ? "#fff" : theme.palette.text.primary,
                borderColor: theme.palette.secondary.main,
              }}
              onClick={() => {
                dispatch(setSelectedStrategy(strategy));
                setOpenCancelModal(true);
                trackEvent("Open Cancel Modal", { id: strategy._id }, "DCA");
              }}>
              Cancel
            </Button>
          )}
        </StyledAccordionDetails>
      </StyledAccordion>
    );
  };

  return (
    <TableContainer sx={{ backgroundColor: theme.palette.background.default }}>
      <Stack
        direction="row"
        justifyContent={"space-between"}
        sx={{
          borderBottom: "1px solid #E5E7E8",
          alignItems: "center",
          margin: "0px 10px",
        }}>
        <StyledTableTitle sx={{ marginBottom: "6px" }}>Scheduled orders</StyledTableTitle>

        {/* Tabs */}
        <FilterSelection
          filterState={filterState}
          setFilterState={setFilterState}
          allStatuses={allStatuses}
        />
        {/* <Stack direction="row" alignItems={"center"} onClick={() => invert()}>
          <StyledFilterLabel>{filterState ? `Active ` : `Cancelled `}</StyledFilterLabel>
          <DcaSwitch stage={filterState ? "on" : "off"} />
        </Stack> */}
      </Stack>

      {isFetching ? (
        <CustomLoadingComponent />
      ) : (
        <>
          {!strategies || strategies?.length === 0 ? (
            <>
              <StyledLabel sx={{ textAlign: "center", padding: "200px 40px", lineHeight: "18px" }}>
                {`You currently have no ${
                  allStatuses[filterState] === "All"
                    ? "Scheduled orders open"
                    : allStatuses[filterState] + " plans"
                }  with us. Set up a plan on the left and track it
            here.`}
              </StyledLabel>
              <StyledHelperContainer>
                <StyledHelperTitle>
                  {`Not sure what a Scheduled order is or how to place one?`}{" "}
                </StyledHelperTitle>
                <StyledHelperLabel>
                  {`Speak to your broker here or view our quick links below`}
                </StyledHelperLabel>
                <Link
                  target="_blank"
                  underline="none"
                  href="https://support.calebandbrown.com/en/articles/9303915-what-is-dca">
                  <StyledRedHelperText text="What is a Scheduled order?" />
                </Link>
                <Link
                  target="_blank"
                  underline="none"
                  href="https://support.calebandbrown.com/en/articles/9303919-how-to-set-up-a-dca-strategy">
                  <StyledRedHelperText text="How to set up a Scheduled order?" />
                </Link>
              </StyledHelperContainer>
            </>
          ) : (
            <>
              {strategies.map((strategy, it: number) => (
                <TableRow strategy={strategy} iterator={it} />
              ))}
              {pageCount && pageCount > 1 && (
                <Pagination
                  sx={{ display: "flex", justifyContent: "center" }}
                  onChange={(_, page) => setPage(page)}
                  count={pageCount}
                  page={page}
                />
              )}
            </>
          )}
        </>
      )}
      <CancelModal open={openCancelModal} setOpen={setOpenCancelModal} />
    </TableContainer>
  );
}
